'use client';

import { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { notFound } from 'next/navigation';
import Dynamic2 from './Dynamic2';
import Page2 from './Page2';
import { decodeContent } from '@/utils';

interface DynamicPageContentProps {
  type: string;
  pageKey: string;
  currentLang: string;
  dict?: any;
}

export interface PageData {
  id: string;
  title: string;
  content: string;
  specifications: any[];
  type: string;
  key: string;
  slug: string;
  isActive: boolean;
}

// Default page data when no data is found
const getDefaultPageData = (type: string, pageKey: string): PageData => {
  // Format the key for display (e.g., "for-caregivers" -> "For Caregivers")
  const formatTitle = (key: string) => {
    return key
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Extract the target audience from the key (e.g., "for-caregivers" -> "Caregivers")
  const getAudience = (key: string) => {
    if (key.startsWith('for-')) {
      return key.replace('for-', '').split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
    }
    return formatTitle(key);
  };

  const audience = getAudience(pageKey);
  const audienceLower = audience.toLowerCase();

  // Create dynamic banner title based on the slug
  const createBannerTitle = (slug: string) => {
    // Special handling for "for-X" pages
    if (slug.startsWith('for-')) {
      const target = audience;
      return `Guiding You to Healthy Aging and <span>Supportive</span> Care.`;
    }
    
    // For other pages, use the formatted title
    const formattedTitle = formatTitle(slug);
    return `Guiding You to <span>${formattedTitle}</span>`;
  };

  // Banner content with dynamic title
  const bannerHTML = `
    <h1>${createBannerTitle(pageKey)}</h1>
    <p>Senior Connect is your trusted guide, connecting seniors, caregivers, veterans, and people with disabilities to health, housing, transportation, and caregiving resources.</p>
  `;

  return {
    id: `default-${pageKey}`,
    title: formatTitle(pageKey),
    content: btoa(bannerHTML),
    specifications: [
      {
        key: 'banner',
        value: '/images/banner/banner.png'
      },
      {
        key: 'section',
        value: [
          {
            title: `Family ${audience}`,
            description: `Caring for your loved one is a life-changing journey that involves both joyful and challenging moments. Finding valuable resources and services for you and your family can make all the difference! Our free membership gives you access to more resources and features like Ask an Expert.`,
            image: '/images/boxes/family_img.png'
          }
        ]
      },
      {
        key: 'sponsers',
        value: [
          
        ]
      },
      {
        key: 'caregiving-resources',
        value: [
          {
            title: 'Free Expert Advice',
            description: 'Utilize Ask an Expert – where you can receive free, confidential expert advice for caregiving related questions and concerns.',
            image: '/images/icons/card_1.png',
            link: '#'
          },
          {
            title: 'Caregiver Tech Tool Finder',
            description: 'The Caregiver Tech Tool Finder gives you independent, unbiased recommendations for the best caregiving apps, devices and other tech.',
            image: '/images/icons/card_2.png',
            link: '#'
          },
          {
            title: 'E-Newsletters',
            description: 'Stay informed through our e-newsletters – timely and relevant information delivered right to your inbox.',
            image: '/images/icons/card_3.png',
            link: '#'
          },
          {
            title: 'Senior Connect Magazine',
            description: 'Read Senior Connect, Central Virginia\'s Guide for Aging Well and Caregiving 2023',
            image: '/images/icons/card_4.png',
            link: '#'
          }
        ]
      },
      {
        key: 'faqs',
        value: [
          {
            question: `How can I get started with having tough ${audienceLower} conversations with my loved ones?`,
            answer: 'There are programs to alleviate the financial burden of family caregiving.'
          },
          {
            question: `Can you provide information about ${audienceLower} technology tools that might be helpful?`,
            answer: 'There are programs to alleviate the financial burden of family caregiving.'
          },
          {
            question: `Do you have resources and information for veterans ${audienceLower}?`,
            answer: 'There are programs to alleviate the financial burden of family caregiving.'
          },
          {
            question: `What financial assistance is available for ${audienceLower}?`,
            answer: 'There are programs to alleviate the financial burden of family caregiving.'
          }
        ]
      }
    ],
    type: type || 'page',
    key: pageKey,
    slug: pageKey,
    isActive: true
  };
};

export default function DynamicPageContent({ type, pageKey, currentLang, dict }: DynamicPageContentProps) {
  const [pageData, setPageData] = useState<PageData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [keyword, setKeyword] = useState('');
  const router = useRouter();

  useEffect(() => {
    const fetchPageData = async () => {
      try {
        const baseUrl = process.env.NEXT_PUBLIC_API_BACKEND || 'http://localhost:8000';
        const url = `${baseUrl}/api/v1/site-data/key/${pageKey}`;
        
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
          cache: 'no-store',
        });

        if (!response.ok) {
          if (response.status === 404) {
            // Use default page data instead of 404
            console.log('📄 [DynamicPage] Data not found, using default page data');
            setPageData(getDefaultPageData(type, pageKey));
            setLoading(false);
            return;
          }
          throw new Error(`Failed to fetch page data: ${response.status}`);
        }

        const result = await response.json();
        
        if (result.success && result.data) {
          setPageData(result.data);
        } else {
          // Use default page data instead of 404
          console.log('📄 [DynamicPage] Invalid data, using default page data');
          setPageData(getDefaultPageData(type, pageKey));
        }
      } catch (err) {
        console.error('Error fetching page data:', err);
        // Use default page data on error
        console.log('📄 [DynamicPage] Error occurred, using default page data');
        setPageData(getDefaultPageData(type, pageKey));
      } finally {
        setLoading(false);
      }
    };

    fetchPageData();
  }, [pageKey]);

  // Helper functions
  const getSpecValue = (key: string, defaultValue: any = null) => {
    if (!pageData?.specifications) return defaultValue;
    const spec = pageData.specifications.find((s: any) => s.key === key);
    return spec?.value || defaultValue;
  };



  const extractTextFromHtml = (html: string) => {
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent || div.innerText || '';
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const base = `/${currentLang}/find-services`;
    const q = keyword.trim();
    if (q) {
      const params = new URLSearchParams();
      params.set('query', q);
      router.push(`${base}?${params.toString()}`);
    } else {
      router.push(base);
    }
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', width: '100%', background: '#f8f9fa', padding: '60px 0' }}>
        <div className="container">
          {/* Header Skeleton */}
          <div style={{
            height: '60px',
            background: 'linear-gradient(90deg, #e0e0e0 25%, #f0f0f0 50%, #e0e0e0 75%)',
            backgroundSize: '200% 100%',
            animation: 'shimmer 1.5s infinite',
            borderRadius: '8px',
            marginBottom: '30px',
            width: '60%',
          }} />
          
          {/* Content Skeleton */}
          {[1, 2, 3, 4].map(i => (
            <div key={i} style={{
              height: '100px',
              background: 'linear-gradient(90deg, #e0e0e0 25%, #f0f0f0 50%, #e0e0e0 75%)',
              backgroundSize: '200% 100%',
              animation: 'shimmer 1.5s infinite',
              borderRadius: '8px',
              marginBottom: '20px',
            }} />
          ))}
        </div>
        
        <style jsx>{`
          @keyframes shimmer {
            0% { background-position: -200% 0; }
            100% { background-position: 200% 0; }
          }
        `}</style>
      </div>
    );
  }

  // Now we always have pageData (either from API or default), so this is just a safety check
  if (!pageData) {
    return null;
  }

  // Decode content
  const decodedContent = decodeContent(pageData.content || '', true);

  // Check if this is a simple static page by looking for page-type specification
  const pageTypeSpec = pageData.specifications?.find((spec: any) => spec.key === 'page-type');
  const isStaticPage = pageTypeSpec?.value === 'static';
  const isBlogPage = pageTypeSpec?.value === 'blog';
  const dynamic2 = pageTypeSpec?.value === 'dynamic-2';
  const page2 = pageTypeSpec?.value === 'page-2';

  // If it's a blog page
  if (isBlogPage) {
    const summary = getSpecValue('summary', '');
    const imageUrl = getSpecValue('image', '');
    const bannerUrl = getSpecValue('banner', '');

    return (
      <div className="blog-page-content">
        {/* Banner Section */}
        {bannerUrl && (
          <section className="blog-banner" style={{
            backgroundImage: `url(${bannerUrl})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            minHeight: '400px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            marginBottom: '60px'
          }}>
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.5)'
            }}></div>
            <div className="container" style={{ position: 'relative', zIndex: 1 }}>
              <div className="row">
                <div className="col-lg-10 offset-lg-1 text-center">
                  <h1 style={{
                    fontSize: '3rem',
                    fontWeight: 'bold',
                    color: '#ffffff',
                    marginBottom: '20px',
                    textShadow: '2px 2px 4px rgba(0,0,0,0.5)'
                  }}>
                    {pageData.title}
                  </h1>
                  {summary && pageData?.isActive && (
                    <p style={{
                      fontSize: '1.25rem',
                      color: '#ffffff',
                      maxWidth: '800px',
                      margin: '0 auto',
                      textShadow: '1px 1px 2px rgba(0,0,0,0.5)'
                    }}>
                      {summary}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Main Content Section */}
        <section className="blog-content-section" style={{ 
          background: '#ffffff',
          padding: '60px 0',
          minHeight: '50vh'
        }}>
          <div className="container">
            <div className="row">
              <div className="col-lg-8 offset-lg-2">
                {/* Featured Image */}
                {imageUrl && (
                  <div style={{ marginBottom: '40px' }}>
                    <img 
                      src={imageUrl} 
                      alt={pageData.title}
                      style={{
                        width: '100%',
                        height: 'auto',
                        borderRadius: '12px',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
                      }}
                    />
                  </div>
                )}

                {/* Blog Content */}
                <div 
                  className="blog-content"
                  style={{
                    fontSize: '1.125rem',
                    lineHeight: '1.8',
                    color: '#383961',
                  }}
                  dangerouslySetInnerHTML={{ __html: decodedContent }}
                />
              </div>
            </div>
          </div>
        </section>

        <style jsx global>{`
          .blog-content h1,
          .blog-content h2,
          .blog-content h3,
          .blog-content h4,
          .blog-content h5,
          .blog-content h6 {
            margin-top: 40px;
            margin-bottom: 20px;
            font-weight: 700;
            color: #2c3e50;
            line-height: 1.3;
          }

          .blog-content h1 { font-size: 2.5rem; }
          .blog-content h2 { font-size: 2rem; }
          .blog-content h3 { font-size: 1.75rem; }
          .blog-content h4 { font-size: 1.5rem; }
          .blog-content h5 { font-size: 1.25rem; }
          .blog-content h6 { font-size: 1.125rem; }

          .blog-content p {
            margin-bottom: 24px;
            color: #383961;
          }

          .blog-content ul,
          .blog-content ol {
            margin-bottom: 24px;
            padding-left: 40px;
          }

          .blog-content li {
            margin-bottom: 12px;
            line-height: 1.8;
          }

          .blog-content a {
            color: #e87722;
            text-decoration: none;
            border-bottom: 1px solid #e87722;
            transition: all 0.3s ease;
          }

          .blog-content a:hover {
            color: #d06619;
            border-bottom-color: #d06619;
          }

          .blog-content img {
            max-width: 100%;
            height: auto;
            border-radius: 8px;
            margin: 30px 0;
            box-shadow: 0 2px 10px rgba(0,0,0,0.1);
          }

          .blog-content blockquote {
            margin: 30px 0;
            padding: 20px 30px;
            border-left: 4px solid #e87722;
            background: #f8f9fa;
            font-style: italic;
            color: #555;
          }

          .blog-content pre {
            background: #f8f9fa;
            padding: 20px;
            border-radius: 8px;
            overflow-x: auto;
            margin: 24px 0;
          }

          .blog-content code {
            background: #f8f9fa;
            padding: 2px 6px;
            border-radius: 4px;
            font-family: 'Courier New', monospace;
            font-size: 0.9em;
          }

          .blog-content table {
            width: 100%;
            border-collapse: collapse;
            margin: 24px 0;
          }

          .blog-content th,
          .blog-content td {
            padding: 12px;
            border: 1px solid #e0e0e0;
            text-align: left;
          }

          .blog-content th {
            background: #f8f9fa;
            font-weight: 600;
          }

          .blog-content hr {
            margin: 40px 0;
            border: none;
            border-top: 2px solid #e0e0e0;
          }

          @media (max-width: 768px) {
            .blog-banner h1 {
              font-size: 2rem !important;
            }
            
            .blog-banner p {
              font-size: 1rem !important;
            }

            .blog-content {
              font-size: 1rem !important;
            }

            .blog-content h1 { font-size: 1.75rem !important; }
            .blog-content h2 { font-size: 1.5rem !important; }
            .blog-content h3 { font-size: 1.25rem !important; }
          }
        `}</style>
      </div>
    );
  }

  // If it's a simple static page (like privacy-policy, terms, etc.)
  if (isStaticPage) {
    return (
      <div className="static-page-content" style={{ 
        minHeight: '100vh', 
        background: '#ffffff',
        padding: '80px 0'
      }}>
        <div className="container">
          <div className="row">
            <div className="col-lg-10 offset-lg-1">
              {/* Page Title */}
              <h1 style={{
                fontSize: '2.5rem',
                fontWeight: 'bold',
                color: '#2c3e50',
                marginBottom: '40px',
                paddingBottom: '20px',
                borderBottom: '2px solid #e0e0e0'
              }}>
                {pageData.title}
              </h1>
              
              {/* Page Content */}
              <div 
                className="page-content"
                style={{
                  fontSize: '1rem',
                  lineHeight: '1.8',
                  color: '#383961',
                }}
                dangerouslySetInnerHTML={{ __html: decodedContent }}
              />
            </div>
          </div>
        </div>

        <style jsx global>{`
          .page-content h1,
          .page-content h2,
          .page-content h3,
          .page-content h4,
          .page-content h5,
          .page-content h6 {
            margin-top: 30px;
            margin-bottom: 15px;
            font-weight: 600;
            color: #2c3e50;
          }

          .page-content h1 { font-size: 2rem; }
          .page-content h2 { font-size: 1.75rem; }
          .page-content h3 { font-size: 1.5rem; }
          .page-content h4 { font-size: 1.25rem; }
          .page-content h5 { font-size: 1.1rem; }
          .page-content h6 { font-size: 1rem; }

          .page-content p {
            margin-bottom: 20px;
          }

          .page-content ul,
          .page-content ol {
            margin-bottom: 20px;
            padding-left: 30px;
          }

          .page-content li {
            margin-bottom: 10px;
          }

          .page-content a {
            color: #4670BF;
            text-decoration: underline;
          }

          .page-content a:hover {
            color: #2c3e50;
          }

          .page-content strong {
            font-weight: 600;
          }

          .page-content blockquote {
            border-left: 4px solid #4670BF;
            padding-left: 20px;
            margin: 20px 0;
            font-style: italic;
            color: #666;
          }

          .page-content table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 20px;
          }

          .page-content table th,
          .page-content table td {
            border: 1px solid #ddd;
            padding: 12px;
            text-align: left;
          }

          .page-content table th {
            background-color: #f4f4f4;
            font-weight: 600;
          }
        `}</style>
      </div>
    );
  }

  if(dynamic2){
    return <Dynamic2 pageData={pageData} dict={dict} lang={currentLang} />
  }
  if(page2){
    return <Page2 pageData={pageData} dict={dict} lang={currentLang} />
  }

  // For complex pages (with specifications), render the full layout
  // Extract data from specifications
  const sponsors = getSpecValue('sponsers', []); // Note: API uses 'sponsers' not 'sponsors'
  const sections = getSpecValue('section', []);
  const caregivingResources = getSpecValue('caregiving-resources', []);
  const faqs = getSpecValue('faqs', []);
  const bannerImage = getSpecValue('banner', '/images/banner/banner.png');

  // Parse the HTML content for banner
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = decodedContent;
  
  // Extract banner title and description from decoded content
  const h1Element = tempDiv.querySelector('h1');
  const pElement = tempDiv.querySelector('p');
  
  const bannerTitle = h1Element?.textContent || pageData.title;
  const bannerDescription = pElement?.textContent || '';

  // Get section data
  const section = sections.length > 0 ? sections[0] : null;
  
  // Split banner title to highlight the word with orange color
  // The HTML has the orange word in a <strong> with specific color
  const bannerTitleHtml = h1Element?.innerHTML || '';
  
  // FAQ title
  const faqTitle = 'Frequently Asked Questions';
  
  console.log('Page Data:', pageData);
  console.log('Sponsors:', sponsors);
  console.log('Sections:', sections);
  console.log('Resources:', caregivingResources);
  console.log('FAQs:', faqs);

  return (
    <>
      {/* Banner Section */}
      <section className="banner-sec bdm">
        <div className="container">
          <div className="row">
            <div className="col-lg-6 col-md-12">
              <div className="main-content">
                <h1 dangerouslySetInnerHTML={{ __html: bannerTitleHtml || bannerTitle }} />
                <p>{bannerDescription}</p>
                <div className="row">
                  <div className="col-lg-5 d-flex align-self-center">
                    <div className="design"></div>
                  </div>
                  <div className="col-lg-7">
                    <div className="sub-heading">
                      <h5>Find the Services You Need</h5>
                    </div>
                  </div>
                </div>
                <form onSubmit={handleSearch}>
                  <div className="country-box">
                    <div className="search-box">
                      <label htmlFor="">
                        <img src="/images/icons/keyword_search.svg" alt="" />
                      </label>
                      <input
                        type="search"
                        className="form-control"
                        placeholder="Search by keyword"
                        value={keyword}
                        onChange={(e) => setKeyword(e.target.value)}
                      />
                    </div>
                    <button type="submit" className="btn btn-secondary">
                      Search
                    </button>
                  </div>
                </form>
                {sponsors && sponsors.length > 0 && (
                  <div className="sponser">
                    <span>Sponsored By</span>
                    {sponsors.map((sponsor: any, index: number) => (
                      <img key={index} src={sponsor.image} alt="Sponsor" />
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="col-lg-6 col-md-12">
              <div className="banner-img">
                <img src={bannerImage} alt="Banner" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Help Section */}
      {section && (
        <section className="help-sec">
          <div className="container">
            <div className="row align-items-center">
              <div className="col-lg-6">
                <div className="help-img">
                  <img src={section.image || '/images/boxes/family_img.png'} alt={section.title} />
                </div>
              </div>
              <div className="col-lg-6 align-self-center">
                <div className="help-content">
                  <h2>{section.title}</h2>
                  <p>{section.description}</p>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Resources Section */}
      {caregivingResources && caregivingResources.length > 0 && (
        <section className="resource-sec">
          <div className="container">
            <div className="row">
              <div className="col-lg-12">
                <div className="resource-heading">
                  <h2>Section Tips</h2>
                </div>
              </div>
              {caregivingResources.map((resource: any, index: number) => (
                <div key={index} className="col-lg-3 col-md-6 col-sm-6 col-6">
                  <div className="resource-card">
                    <div className="card-img">
                      <img src={resource.image || '/images/icons/card_1.png'} alt={resource.title} />
                    </div>
                    <div className="card-conttent">
                      <h4>
                        <a href={resource.link || '#'}>{resource.title}</a>
                      </h4>
                      <p>{resource.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FAQ Section */}
      {faqs && faqs.length > 0 && (
        <section className="faq-sec">
          <div className="container">
            <div className="row">
              <div className="col-lg-12">
                <div className="faq-heading">
                  <h2>{faqTitle}</h2>
                </div>
                <div className="accordion" id="care-accordion">
                  {faqs.map((faq: any, index: number) => {
                    const accordionId = `heading${index}`;
                    const collapseId = `collapse${index}`;
                    
                    return (
                      <div key={index} className="accordion-item">
                        <h2 className="accordion-header" id={accordionId}>
                          <button
                            className="accordion-button collapsed"
                            type="button"
                            data-bs-toggle="collapse"
                            data-bs-target={`#${collapseId}`}
                            aria-expanded="false"
                            aria-controls={collapseId}
                          >
                            {faq.question || faq.title}
                          </button>
                        </h2>
                        <div
                          id={collapseId}
                          className="accordion-collapse collapse"
                          aria-labelledby={accordionId}
                          data-bs-parent="#care-accordion"
                        >
                          <div className="accordion-body">
                            <div className="service-details">
                              {faq.answer || faq.content}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </section>
      )}
    </>
  );
}

